import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import viewedCardsService from '../services/viewedCardsService';

export const useViewedCards = (cardId = null) => {
  const { currentUser } = useAuth();
  const [isViewed, setIsViewed] = useState(false);
  const [viewedSet, setViewedSet] = useState(new Set());
  const expandTimerRef = useRef(null);

  const loadViewedStatus = useCallback(async () => {
    if (!cardId) {
      const cards = await viewedCardsService.getViewedCards();
      setViewedSet(new Set(cards));
    } else {
      const viewed = await viewedCardsService.isCardViewed(cardId);
      setIsViewed(viewed);
    }
  }, [cardId]);

  useEffect(() => {
    if (currentUser?.uid) {
      viewedCardsService.setUserId(currentUser.uid);
      loadViewedStatus();
    } else {
      setIsViewed(false);
      setViewedSet(new Set());
    }

    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
      }
    };
  }, [currentUser?.uid, loadViewedStatus]);

  const trackExpansion = useCallback((expanded) => {
    if (!expanded || !cardId || isViewed) return;

    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
    }

    expandTimerRef.current = setTimeout(async () => {
      const success = await viewedCardsService.markCardAsViewed(cardId);
      if (success) {
        setIsViewed(true);
        setViewedSet(prev => new Set([...prev, cardId]));
      }
    }, 2000);
  }, [cardId, isViewed]);

  const checkIfViewed = useCallback((id) => {
    return viewedSet.has(id);
  }, [viewedSet]);

  return {
    isViewed,
    trackExpansion,
    viewedSet,
    checkIfViewed
  };
};